const MONO = "JetBrains Mono, monospace";

const capsulePath = (cx, cy, rx, ry) => {
  const r = Math.min(rx, ry);
  const s = Math.max(rx, ry) - r;
  return `M ${cx - s} ${cy - r} H ${cx + s} A ${r} ${r} 0 0 1 ${cx + s} ${cy + r} H ${cx - s} A ${r} ${r} 0 0 1 ${cx - s} ${cy - r} Z`;
};

export const CompartmentDiagram = ({ shape, wCm, hCm, compStats, labels, showEndView = true }) => {
  const W = 480;
  const H = 380;
  const n = compStats.length || 1;

  // end view (shared cross-section) — fill shown at overall average
  const cx = 120;
  const cy = 170;
  const wD = shape === "stadium" ? Math.max(wCm, hCm) : wCm;
  const hD = shape === "stadium" ? Math.min(wCm, hCm) : hCm;
  const scale = 100 / (Math.max(wD, hD) / 2);
  const rx = (wD / 2) * scale;
  const ry = (hD / 2) * scale;
  const avgFrac = compStats.length ? compStats.reduce((s, c) => s + c.frac, 0) / compStats.length : 0;
  const surfaceY = cy + ry - avgFrac * 2 * ry;

  // side view (compartments) — centered when no end view
  const sx = showEndView ? 260 : (W - 200) / 2;
  const sw = 200;
  const sy = 92;
  const sh = 195;
  const segW = sw / n;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-[480px] mx-auto select-none" data-testid="compartment-diagram">
      <defs>
        <clipPath id="compClip">
          {shape === "rectangular" ? (
            <rect x={cx - rx} y={cy - ry} width={2 * rx} height={2 * ry} rx="6" />
          ) : shape === "stadium" ? (
            <path d={capsulePath(cx, cy, rx, ry)} />
          ) : (
            <ellipse cx={cx} cy={cy} rx={rx} ry={ry} />
          )}
        </clipPath>
      </defs>

      {/* end view */}
      {showEndView && (
        <>
          <text x={cx} y={cy - ry - 14} textAnchor="middle" fontSize="9.5" fill="#64748B" fontFamily={MONO}>END VIEW</text>
          {shape === "rectangular" ? (
            <rect x={cx - rx} y={cy - ry} width={2 * rx} height={2 * ry} rx="6" fill="#0D131E" stroke="#3B4B63" strokeWidth="2" />
          ) : shape === "stadium" ? (
            <path d={capsulePath(cx, cy, rx, ry)} fill="#0D131E" stroke="#3B4B63" strokeWidth="2" />
          ) : (
            <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="#0D131E" stroke="#3B4B63" strokeWidth="2" />
          )}
          {avgFrac > 0.001 && (
            <g clipPath="url(#compClip)">
              <rect x={cx - rx} y={surfaceY} width={2 * rx} height={cy + ry - surfaceY} fill={compStats[0]?.color || "#3B82F6"} opacity="0.4" />
            </g>
          )}
          <text x={cx} y={cy + ry + 24} textAnchor="middle" fontSize="10" fill="#94A3B8" fontFamily={MONO}>{labels.crossW}</text>
          {labels.crossH && (
            <text x={cx} y={cy + ry + 40} textAnchor="middle" fontSize="10" fill="#94A3B8" fontFamily={MONO}>{labels.crossH}</text>
          )}
        </>
      )}

      {/* side view with compartments */}
      <text x={sx + sw / 2} y={sy - 34} textAnchor="middle" fontSize="9.5" fill="#64748B" fontFamily={MONO}>SIDE VIEW · {n} COMPARTMENT{n > 1 ? "S" : ""}</text>
      <rect x={sx} y={sy} width={sw} height={sh} rx="10" fill="#0D131E" stroke="#3B4B63" strokeWidth="2" />
      {compStats.map((c, i) => {
        const x = sx + i * segW;
        const fillH = c.frac * sh;
        return (
          <g key={i} data-testid={`comp-seg-${i}`}>
            <clipPath id={`segClip${i}`}>
              <rect x={x + 1} y={sy + 1} width={segW - 2} height={sh - 2} rx="6" />
            </clipPath>
            {c.frac > 0.001 && (
              <rect x={x + 1} y={sy + sh - fillH} width={segW - 2} height={fillH} fill={c.color} opacity="0.75" clipPath={`url(#segClip${i})`} />
            )}
            {c.frac > 0.001 && c.frac < 0.999 && (
              <line x1={x + 2} y1={sy + sh - fillH} x2={x + segW - 2} y2={sy + sh - fillH} stroke={c.color} strokeWidth="2" />
            )}
            {i > 0 && <line x1={x} y1={sy} x2={x} y2={sy + sh} stroke="#3B4B63" strokeWidth="3" />}
            <text x={x + segW / 2} y={sy - 16} textAnchor="middle" fontSize="8.5" fill="#64748B" fontFamily={MONO}>C{i + 1}</text>
            <text x={x + segW / 2} y={sy + sh + 16} textAnchor="middle" fontSize="9" fill={c.frac >= 0.95 ? "#EF4444" : "#94A3B8"} fontFamily={MONO}>{Math.round(c.frac * 100)}%</text>
            <text x={x + segW / 2} y={sy + sh + 30} textAnchor="middle" fontSize="8" fill="#64748B" fontFamily={MONO}>{c.liters.toFixed(0)}L</text>
          </g>
        );
      })}
      <text x={sx + sw / 2} y={sy + sh + 48} textAnchor="middle" fontSize="10" fill="#94A3B8" fontFamily={MONO}>{labels.len}</text>
    </svg>
  );
};
